import { StytchProvider } from "@stytch/react";
import React, { useEffect, useMemo, useState } from "react";

import { setSessionRecovery, setTokenSource } from "@/shared/api/client";

import { COOKIE_DOMAIN, getStytch, SESSION_DURATION_MINUTES } from "../client";
import { recoverSession, sessionJwt } from "../session";
import { signedInElsewhere } from "../shared-session";

import type { StytchClient } from "../client";

/* Wraps StytchProvider and is the seam that keeps `shared/api/client.ts` from
   importing a feature: it pushes the token getter and the 401 recovery step
   into the http client instead.

   Registration happens during render, not in an effect — a child's mount effect
   can fire a request before a parent's effect has run, and that request would
   go out unauthenticated.

   Someone who signed in on another Definable host arrives with the session
   cookie but no session in this origin's storage. Nothing renders until the SDK
   has fetched it: RequireAuth would otherwise send them to /login, and the site
   straight back here (shared-session.ts). */
export default function AuthProvider({
  children,
  client = getStytch(),
}: {
  children: React.ReactNode;
  /** Stories and tests pass a stand-in; the app uses the real singleton. */
  client?: StytchClient;
}) {
  useMemo(() => {
    setTokenSource(() => sessionJwt(client));
    setSessionRecovery(() => recoverSession(client));
  }, [client]);

  const [fetching, setFetching] = useState(() => signedInElsewhere(COOKIE_DOMAIN, !!client.session.getSync()));
  useEffect(() => {
    if (!fetching) return;
    // A cookie that no longer opens a session is the SDK's to clear; they land signed out.
    client.session
      .authenticate({ session_duration_minutes: SESSION_DURATION_MINUTES })
      .catch(() => undefined)
      .finally(() => setFetching(false));
  }, [client, fetching]);

  if (fetching) return null;
  return <StytchProvider stytch={client}>{children}</StytchProvider>;
}
